import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';
import { satisfies, valid } from 'semver';

import { Notification } from '@xsolla/ui-core';
import { AlertExclamation } from '@xsolla/ui-icons';
import { FileUploader, UploaderValue } from '@xsolla/ui-uploader';

import {
  getOAuthSettings,
  getOldLoginWidgetParams,
  getOldLoginWidgetRequest,
  putOldLoginWidgetRequest,
  UploadFileType,
  uploadMerchantFile
} from '../../../api';
import { useConfig } from '../../../contexts/config';
import { useCurrentProject } from '../../../contexts/settings';
import { useNotifications } from '../../../hooks/useNotifications';
import { RouteParams } from '../../../routes/types';
import { ShortSettingsBlock, VerticalHorizontalCenterBlock } from '../../../styled';
import { NotificationLink } from '../../Authentication/EnterpriseConnections/style';
import { AgreementNotification, CodeEditor, HeaderTitle, Preloader } from '../../ui';
import { init, initialState, widgetCustomizationReducer } from '../WidgetPreviewFrame/reducer';
import { ActionType, FileStatus } from '../WidgetPreviewFrame/types';
import { getFileNameFromURL } from '../WidgetPreviewFrame/utils';
import { WidgetSettings } from '../WidgetPreviewFrame/WidgetSettings';
import { getExampleCode } from './settings';
import { BlockWrapper, CustomizationFrameWrapper, UploadWrapper } from './styles';

const NOT_FILE = {
  url: '',
  filename: ''
};

type Props = RouteComponentProps & {
  isAgreementSigned: boolean;
};

const OldWidgetCustomizeFrame = ({ match, isAgreementSigned }: Props) => {
  const intl = useIntl();
  const { projectId } = match.params as RouteParams;
  const { merchantId } = useConfig();
  const { currentProjectType } = useCurrentProject(projectId);
  const { showErrorNotify } = useNotifications();

  const [state, dispatch] = useReducer(widgetCustomizationReducer, initialState, () =>
    init(isAgreementSigned, intl)
  );
  const { fetching, widgetPreviewSettings, oldLoginWidget, oauth2 } = state;
  const { language, country } = widgetPreviewSettings;
  const { widgetStyle, uploadStatus, uploadFileValue } = oldLoginWidget;

  useEffect(() => {
    Promise.all([
      getOldLoginWidgetRequest(projectId),
      getOAuthSettings(projectId),
      getOldLoginWidgetParams()
    ])
      .then(([oldLoginWidgetStyle, oauthSettings, widgetSettings]) => {
        const { custom_css } = oldLoginWidgetStyle;
        const { languages, countries } = widgetSettings;

        if (custom_css) {
          dispatch({
            type: ActionType.setOldWidgetFileValue,
            payload: {
              url: custom_css,
              filename: getFileNameFromURL(custom_css)
            }
          });
        }

        dispatch({ type: ActionType.setOldWidgetStyle, payload: oldLoginWidgetStyle });
        dispatch({ type: ActionType.setLanguageOptions, payload: languages });
        dispatch({ type: ActionType.setCountryOptions, payload: countries });
        dispatch({ type: ActionType.setOauth2Options, payload: oauthSettings.clients });
      })
      .catch((error) => {
        showErrorNotify(error);
      })
      .finally(() => dispatch({ type: ActionType.setFetching, payload: false }));
  }, [projectId]);

  const additionalParams = useMemo(
    () => ({
      ...(oauth2.currOAuth2 && {
        clientId: oauth2.currOAuth2.id ? oauth2.currOAuth2.id : undefined,
        responseType: `${oauth2.currOAuth2.response_types[0]}`,
        state: 'yourStateHere',
        redirectUri: `${oauth2.currOAuth2.redirect_uris[0]}`
      })
    }),
    [oauth2.currOAuth2]
  );

  const validateCSSFile = useCallback(
    async (file) => {
      return new Promise<boolean>((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = async (e) => {
          const text = e && e.target ? String(e.target.result) : '';
          const [name, version] = text.match(/xsolla-widget-login version: ([\w-.]+)/) || [];
          let cssVersion;

          if (name && version && valid(version)) {
            cssVersion = version;
          } else {
            cssVersion = 'v1.0.0';
          }

          const previewVersion = widgetStyle ? widgetStyle.latest_version : '';
          if (cssVersion && satisfies(cssVersion, `= ${widgetStyle?.version}`)) {
            resolve(true);
            // Сохранить файл
          } else {
            reject(
              new Error(
                intl.formatMessage(
                  { id: 'app.validation.old-widget-version-incorrect' },
                  { widgetVersion: previewVersion, cssVersion }
                )
              )
            );
          }
        };
        fr.readAsText(file);
      });
    },
    [widgetStyle]
  );

  const uploadFileHandler = useCallback(
    async (file) => {
      dispatch({ type: ActionType.setOldWidgetFileStatus, payload: FileStatus.IDLE_STATUS });
      return new Promise<UploaderValue>(() =>
        validateCSSFile(file)
          .then(() => {
            const formData = new FormData();
            formData.append('file', file);
            dispatch({
              type: ActionType.setOldWidgetFileStatus,
              payload: FileStatus.LOADING_STATUS
            });

            uploadMerchantFile(merchantId, formData).then(({ file_url }: UploadFileType) => {
              if (file_url) {
                putOldLoginWidgetRequest(projectId, file_url, null)
                  .then(() => {
                    dispatch({
                      type: ActionType.setOldWidgetFileStatus,
                      payload: FileStatus.SUCCESS_STATUS
                    });
                    dispatch({
                      type: ActionType.setOldWidgetFileValue,
                      payload: {
                        url: file_url,
                        filename: file.name
                      }
                    });
                    return uploadFileValue;
                  })
                  .catch(() => {
                    dispatch({
                      type: ActionType.setOldWidgetFileStatus,
                      payload: FileStatus.ERROR_STATUS
                    });
                    dispatch({ type: ActionType.setOldWidgetFileValue, payload: NOT_FILE });
                    return NOT_FILE;
                  });
              }
            });
          })
          .catch(() => {
            dispatch({ type: ActionType.setOldWidgetFileStatus, payload: FileStatus.ERROR_STATUS });
            return NOT_FILE;
          })
      );
    },
    [widgetStyle]
  );

  const onDeleteCss = useCallback((): Promise<void> => {
    dispatch({ type: ActionType.setOldWidgetFileStatus, payload: FileStatus.LOADING_STATUS });
    return putOldLoginWidgetRequest(projectId, '', null)
      .then(() => {
        dispatch({ type: ActionType.setOldWidgetFileStatus, payload: FileStatus.SUCCESS_STATUS });
        dispatch({ type: ActionType.setOldWidgetFileValue, payload: NOT_FILE });
      })
      .catch(() => {
        dispatch({ type: ActionType.setOldWidgetFileStatus, payload: FileStatus.ERROR_STATUS });
      });
  }, [uploadStatus]);

  if (!currentProjectType || fetching || !widgetStyle) {
    return (
      <VerticalHorizontalCenterBlock>
        <Preloader />
      </VerticalHorizontalCenterBlock>
    );
  }

  return (
    <>
      {!isAgreementSigned && <AgreementNotification />}

      <HeaderTitle
        title={intl.formatMessage({ id: 'widget-customization.settings.title' })}
        subtitle={intl.formatMessage({ id: 'widget-customization.settings.subtitle' })}
      />

      <UploadWrapper>
        <ShortSettingsBlock>
          <BlockWrapper>
            <Notification icon={AlertExclamation}>
              {intl.formatMessage(
                { id: 'widget-customization.old.instruction' },
                {
                  ul: (chunk: string) => (
                    <ul style={{ padding: '0 0 0 12px', margin: '0' }}>{chunk}</ul>
                  ),
                  ol: (chunk: string) => <ol style={{ padding: '0', margin: '0' }}>{chunk}</ol>,
                  a1: (chunk: string) => (
                    <NotificationLink
                      href={
                        widgetStyle.is_latest ? widgetStyle.latest_zip_url : widgetStyle.zip_url
                      }
                      target="_blank"
                      rel="noopener noreferrer">
                      {chunk} {!widgetStyle.is_latest ? widgetStyle.version : ''}
                    </NotificationLink>
                  ),
                  a2: (chunk: string) => (
                    <NotificationLink href="https://github.com/xsolla/xsolla-login-js-sdk#customizing-widget-style">
                      {chunk}
                    </NotificationLink>
                  )
                }
              )}
            </Notification>
          </BlockWrapper>
          <BlockWrapper>
            <FileUploader
              disabled={uploadStatus === FileStatus.LOADING_STATUS}
              options={{
                uploadFn: uploadFileHandler,
                deleteFn: onDeleteCss,
                accept: '.css'
              }}
              placeholder={intl.formatMessage({
                id: 'widget-customization.old.upload_css.placeholder'
              })}
              value={uploadFileValue}
            />
          </BlockWrapper>
          {/* Обновление версии старого виджета. Вопрос - надо ли? Или лучше мотивировтаь партнеров переходить на новый виджет
            !widgetStyle.is_latest && (
              <BlockWrapper>
                <Notification
                  icon={AlertExclamation}
                  caption={
                    <>
                      {intl.formatMessage({id: 'widget-customization.old.updateNotifications.message'})}
                      <br />
                      {intl.formatMessage({id: 'widget-customization.old.updateNotifications.updating-alert'})}
                    </>
                  }
                >
                  {intl.formatMessage({id: 'widget-customization.old.updateNotifications.title'})}
                </Notification>
              </BlockWrapper>
            )
            */}
        </ShortSettingsBlock>
      </UploadWrapper>

      <CustomizationFrameWrapper>
        <CodeEditor
          disabled
          allowCopyCode
          initialValue={getExampleCode({
            projectId,
            country,
            language,
            version: widgetStyle.version,
            ...additionalParams
          })}
          showLineNumbers
        />
      </CustomizationFrameWrapper>

      <WidgetSettings state={state} dispatch={dispatch} />
    </>
  );
};

export default withRouter(OldWidgetCustomizeFrame);
